import { ActivatedRoute, NavigationEnd, NavigationExtras, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Injectable } from '@angular/core';
import { Title } from '@angular/platform-browser';

import {
  IAdviceSet,
  IQuestion,
  ISelfServMeta,
  RouteHistoryService,
  SelfServData,
  SelfServDataService,
  SelfServMetaStatus,
  SubscriberComponent,
  AppConfig
} from '@aa/common';

import { QnaStage, SelfServMetaCache } from '@aa/selfserv/shared';
import { BehaviorSubject } from 'rxjs';

export interface IBreadcrumb {
  title: string;
  route?: string;
  href?: string;
}

@Injectable({providedIn: 'root'})
export class QnaNavigationService extends SubscriberComponent {

  public readonly baseUrl: string;
  private readonly originalTitle: string;
  public readonly meta: ISelfServMeta;
  public readonly hotjarId: number;

  public data: SelfServData = null;
  public error: string = null;
  public url: string;

  public stage: QnaStage = QnaStage.None;
  public showBanner: boolean = true;
  public breadcrumbs: IBreadcrumb[] = [];

  public subPage: string = null;

  private readonly stageSource = new BehaviorSubject<QnaStage>(QnaStage.None);
  public readonly stage$ = this.stageSource.asObservable();


  constructor(private readonly location: Location,
              private readonly router: Router,
              private readonly routeHistory: RouteHistoryService,
              private readonly titleService: Title,
              appConfig: AppConfig,
              metaCache: SelfServMetaCache,
              dataService: SelfServDataService) {

    super();

    this.hotjarId = appConfig.hotjarId;
    this.originalTitle = titleService.getTitle();


    this.meta = metaCache.meta;

    // baseUrl
    this.baseUrl = window.location.origin + location.prepareExternalUrl('');

    // no data
    if (!this.meta) {
      this.redirectTo404();
      return;
    }

    // invalid data
    if (this.meta.metaStatus === SelfServMetaStatus.Error) {
      this.setError('Meta data invalid');
      return;
    }

    /* TODO: redirect to correct team url by replacing current route state
    if(this.data.domain && this.data.domain != route.path[0]) {
      this.changeTeamId(this.data.teamId);
      return;
    }
    */

    // resolve data from meta
    dataService.getDataFromMeta(this.meta).subscribe(data => {
      this.data = data;
    }, error => this.setError(error));


    // monitor url changes
    super.subscribe(router.events, evt => {
      if (evt instanceof NavigationEnd) {
        this.updateUrl();
      }
    });
    this.updateUrl();
  }

  public static serializePath(path: number[]) {
    if (!path) {
      return path;
    }

    return path.join(',');
  }


  public setTitle(page: string) {
    const title = page ? `${page} | ${this.originalTitle}` : this.originalTitle;
    this.titleService.setTitle(title);
  }

  private updateUrl() {
    this.url = window.location.origin + this.router.url;
  }

  public redirectTo404() {
    this.router.navigate(['/not-found'], { skipLocationChange: true });
  }

  public setError(error: string) {
    this.data = null;
    this.error = error;
  }

  public print() {
    window.print();
    return false; // to prevent navigation
  }

  public setStage(stage: QnaStage, subPage: string = null) {
    this.stage = stage;
    this.subPage = subPage;
    this.rebuildBreadcrumbs();

    this.stageSource.next(stage);
  }

  public setCurrentSubPage(title: string) {
    this.subPage = title;
    // this.rebuildBreadcrumbs();
  }

  private rebuildBreadcrumbs() {
    const breadcrumbs: IBreadcrumb[] = [];

    if(this.meta.homeLink) {
      breadcrumbs.push({title: "Home", href: this.meta.homeLink});
    }

    if(this.meta.housingLink) {
      breadcrumbs.push({title: "Housing", href: this.meta.housingLink});
    }


    breadcrumbs.push({title: this.meta.serviceName, route: '/start'});

    if (this.stage >= QnaStage.Questions) {
      breadcrumbs.push({title: 'Questions', route: '/questions'});
    }

    if (this.stage >= QnaStage.Advice) {
      breadcrumbs.push({title: 'Advice', route: '/advice'});
    }

    this.breadcrumbs = breadcrumbs;
  }


  public scrollTop() {
    window.scrollTo({top: 0, behavior: 'auto'});
  }

  public goToStart(): boolean {
    return this.navigate(['start']);
  }

  /**
   * Use browser history if previous state was questions.
   * otherwise navigate directly
   */
  public goToQuestions(question: IQuestion): boolean {

    if (question != null) {
      const questionId = question.slug || question.id;
      return this.navigate(['questions', questionId]);
    }

    const currentUrl = this.routeHistory.getCurrentUrl();
    const prevUrl = this.routeHistory.getPreviousUrl();
    if (!currentUrl.includes('questions') && prevUrl && prevUrl.includes('questions')) {
      return this.back();
    }

    return this.navigate(['questions']);
  }

  private navigate(commands: string[]) {
    const extras: NavigationExtras =  {
      // relativeTo: this.route, //uncomment this if module is used as sub page
    };

    this.router.navigate(commands, extras);
    return false;
  }


  public back() {
    this.location.back();
    return false;
  }

  public getQuestionHrefById(id: Guid): string {

    const question = this.data.getQuestion(id);
    if (!question) {
      console.warn('getQuestionHref: failed to resolve question', id);
      return null;
    }

    return this.getQuestionHref(question);
  }

  public getQuestionHref(question: IQuestion): string {

    if (!question) {
      return null;
    }

    const slug = question.slug || question.id;
    return `/questions/${slug}`;
  }

  public getAdviceSetHrefById(id: Guid): string {
    const adviceSet = this.data.getAdviceSet(id);

    if (!adviceSet) {
      console.warn('getAdviceSetHref: failed to resolve adviceSet', id);
      return null;
    }

    return this.getAdviceSetHref(adviceSet);
  }

  public getAdviceSetHref(adviceSet: IAdviceSet): string {

    if (!adviceSet) {
      return null;
    }

    const slug = adviceSet.slug || adviceSet.id;
    return `/advice/${slug}`;
  }
}
